import { formatNumber, isStringPresent } from '@emma-helpers/format.helper';
import { DEFAULT_CURRENCY } from '@platform/helpers/currency.helper';
import { get, isObject, uniq } from 'lodash';
import { CellCommonTypes, CellParam, EMMATableColumn } from './emma-table.types';

export const GENERIC_EXPORT_FILENAME = 'EMMA_exported_data';
export const INPUT_FILTER_DEBOUNCE_DELAY = 300;

export enum COLUMN_DATA_TYPE {
  ACTION = 'action',
  ACTION_ICON_LIST = 'actionIconList',
  BOOLEAN = 'boolean',
  CLIPBOARD = 'clipboard',
  CURRENCY = 'currency',
  DATE = 'date',
  DEFAULT = 'default',
  HTML = 'html',
  ICON = 'icon',
  ICON_LIST = 'iconList',
  ICON_OPTION = 'iconOption', // Requires OPTION_LIST
  LIST = 'list',
  LIST_LENGTH = 'listLength',
  NUMBER = 'number',
  OPTION = 'option',
  OPTION_LIST = 'optionList',
  PARAMS = 'params',
  PERCENT = 'percent',
  RATIO = 'ratio',
  STARS = 'stars',
  STRING = 'string',
  TIME_WINDOW = 'timeWindow',
  URL = 'url',
  YESNO = 'yesno',
  QR_CODE = 'qr_code',
  PRIORITY = 'priority',
  PERCENTAGE_VALUE = 'percentageValue',
  TAGS = 'tags',
}

export enum COLUMN_SORT_DIRECTION {
  ASC = 1,
  NONE = 0,
  DESC = -1,
}

export const NUMBER_TYPES = [
  COLUMN_DATA_TYPE.NUMBER,
  COLUMN_DATA_TYPE.PERCENT,
  COLUMN_DATA_TYPE.CURRENCY,
  COLUMN_DATA_TYPE.RATIO,
];

export enum CALCULATOR_METHOD {
  SUM = 'sum',
  AVERAGE = 'average',
}

export enum SUMMARY_POSITION {
  TOP = 'top',
  BOTTOM = 'bottom',
}

const filterValid = (v: any) => {
  return v !== undefined && v !== null && v !== '';
};

export function formatDataTypeList(list: string[] | number[] | undefined, separator = ', ', key = '') {
  if (Array.isArray(list) && list.length) {
    if (key) {
      return list.map((item) => (isObject(item) ? String(item[key]) : '')).join(separator);
    } else {
      return list.map((item) => item.toString()).join(separator);
    }
  }
  return '';
}
export function formatDataTypeNumber(value: CellCommonTypes) {
  if (value === null) {
    return '-';
  } else if (value !== undefined) {
    return formatNumber(Number(value));
  }
  return '';
}
export function formatDataTypeCurrency(value: CellCommonTypes, currency = DEFAULT_CURRENCY) {
  if (value === null) {
    return '-';
  } else if (value !== undefined) {
    return formatNumber(Number(value), {
      style: 'currency',
      currency,
    });
  }
  return '';
}
export function formatDataTypePercent(value: CellCommonTypes) {
  if (value === null) {
    return '-';
  } else if (value !== undefined) {
    return formatNumber(Number(value), { style: 'percent' });
  }
  return '';
}
export function formatDataTypeParams(value: CellParam[] | null = []) {
  if (value === null) {
    return '-';
  } else if (value && value.length) {
    return value
      .map((val) => `${encodeURIComponent(val.param_key)}=${encodeURIComponent(val.param_value)}`)
      .join('&');
  }
  return '';
}

export function formatDataTypeOption(
  value: any,
  options: Array<{ [k: string]: any }>,
  valueKey = 'value',
  labelKey = 'label'
) {
  const option = options.find((op) => op[valueKey] === value);
  if (option) {
    return String(option[labelKey]);
  }
  return value;
}
export function formatDataTypeOptionList(
  list: any[] | undefined,
  options: Array<{ [k: string]: any }>,
  separator = ', ',
  valueKey = 'value',
  labelKey = 'label'
) {
  if (list && list.length) {
    return list.map((value) => formatDataTypeOption(value, options, valueKey, labelKey)).join(separator);
  }
  return '';
}

export const summarySum = (cells: number[]) => {
  return cells.reduce((sum, cell) => sum + cell || 0, 0);
};

export const summaryCount = (cells: any[]) => {
  return uniq(cells.filter(filterValid)).length;
};

export const summaryCountArray = (cells: any[]) => {
  return uniq(cells.filter(filterValid).flat()).length;
};

export const summaryAverage = (cells: any[]) => {
  const length = cells.filter(filterValid).length;
  return length ? summarySum(cells) / length : 0;
};

/**
 * Calculate the value of an Array of values, based on the column data type.
 * Auxiliary function for `_processGroupRow` and `updateSummaryCache`.
 */
export const getColumnValue = (values: any[], col: EMMATableColumn, method?: string) => {
  if (!col.dataType || col.dataType === COLUMN_DATA_TYPE.DEFAULT) {
    return summaryCount(values);
  } else if ([COLUMN_DATA_TYPE.NUMBER, COLUMN_DATA_TYPE.CURRENCY].includes(col.dataType)) {
    if (method === 'avg') {
      return summaryAverage(values);
    } else {
      return summarySum(values);
    }
  } else if (
    [COLUMN_DATA_TYPE.PERCENT, COLUMN_DATA_TYPE.RATIO, COLUMN_DATA_TYPE.TIME_WINDOW].includes(col.dataType)
  ) {
    return summaryAverage(values);
  } else if (col.dataType === COLUMN_DATA_TYPE.LIST) {
    return summaryCountArray(values);
  } else if (col.dataType === COLUMN_DATA_TYPE.BOOLEAN) {
    return summarySum(values.map((v) => Number(Boolean(v)))) ? '1' : '';
  } else if (col.dataType === COLUMN_DATA_TYPE.ACTION) {
    return values.length;
  }
  return '';
};

/**
 * Calculate the summary of an Array of values, based on the column data type.
 * Auxiliary function for `_processGroupRow` and `updateSummaryCache`.
 */
export const getColumnSummary = (values: any[], col: EMMATableColumn, method?: CALCULATOR_METHOD) => {
  const value = getColumnValue(values, col, method);
  if (!col.dataType || col.dataType === COLUMN_DATA_TYPE.DEFAULT || col.dataType === COLUMN_DATA_TYPE.LIST) {
    const lengthString = formatDataTypeNumber(value);
    if (col.units) {
      return `${lengthString} ${value === 1 ? col.unit || col.units : col.units}`;
    }
    return lengthString;
  } else {
    return value;
  }
};

export function filterRowFactory<T = any>(
  filterBy: string | string[],
  inputFilter: string
): (row: T) => boolean {
  if (typeof filterBy === 'string') {
    return (row: T) => isStringPresent(inputFilter, get(row, filterBy));
  } else {
    return (row: T) => filterBy.some((field) => isStringPresent(inputFilter, get(row, field)));
  }
}

function formatDataLabelValue(value: { label: string }): string {
  if (isObject(value) && value['label']) {
    return value['label'];
  }
  return '';
}

export function formatDataTypeActionIconList(value: { label: string }) {
  return formatDataLabelValue(value);
}

export function formatDataTypeAction(value: CellCommonTypes | { label: string }) {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }
  return formatDataLabelValue(value);
}

export function formatBoolean(value: boolean): string {
  return value ? '1' : '0';
}
